import { Link } from "react-router-dom";
import { siteInfo } from "@/mocks/site";
import type { NavLinkItem, NavSubLink } from "@/mocks/site";

const footerLinks: { label: string; to: string; sub?: { label: string; to: string }[] }[] = [
  {
    label: "회사소개",
    to: "/about/overview",
    sub: [
      { label: "CEO 인사말", to: "/about/ceo" },
      { label: "회사개요", to: "/about/overview" },
      { label: "회사연혁", to: "/about/history" },
      { label: "인증현황", to: "/about/certifications" },
      { label: "찾아오는 길", to: "/about/location" },
    ],
  },
  { label: "사업소개", to: "/business" },
  { label: "제품소개", to: "/products" },
  { label: "기술자료", to: "/technical" },
  { label: "고객지원", to: "/support" },
  { label: "견적요청", to: "/quotation" },
  { label: "문의하기", to: "/contact" },
  { label: "채용정보", to: "/recruitment" },
];

export default function Footer() {
  return (
    <footer className="bg-foreground-950 text-background-50">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <span className="h-[46px] w-[80px] rounded-md bg-background-50 flex items-center justify-center overflow-hidden">
                <img
                  src="https://storage.readdy-site.link/project_files/e8666665-2242-48cf-9593-4ebcd3ad786e/6437e91a-c2b1-4345-9a35-15b2da6f9f43_compressed_logo.webp"
                  alt="한국고무공업 로고"
                  className="h-full w-full object-contain"
                  style={{ transform: "scaleY(1.18)" }}
                />
              </span>
              <div className="leading-tight">
                <div className="font-heading font-bold text-lg">{siteInfo.brandKo}</div>
                <div className="text-[11px] tracking-[0.2em] uppercase text-background-50/60">
                  {siteInfo.brandEn}
                </div>
              </div>
            </div>
            <p className="mt-6 text-sm text-background-50/70 leading-relaxed max-w-md">
              한국고무공업주식회사는 다양한 산업 분야의 고무제품을 개발·생산하며,
              고객이 겪는 문제를 함께 해결하는 기술 중심 제조기업입니다.
            </p>
            <div className="mt-8 flex items-center gap-3">
              {["ri-global-line", "ri-linkedin-fill", "ri-youtube-fill", "ri-mail-fill"].map(
                (icon) => (
                  <button
                    key={icon}
                    type="button"
                    className="w-10 h-10 rounded-full border border-background-50/30 hover:border-accent-400 hover:text-accent-300 flex items-center justify-center transition-colors cursor-pointer"
                  >
                    <i className={`${icon} text-sm`} />
                  </button>
                ),
              )}
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="text-[11px] tracking-[0.2em] uppercase text-background-50/50 font-semibold mb-5">
              SITEMAP
            </div>
            <div className="flex flex-col gap-2">
              {footerLinks.map((l) => (
                <div key={l.label}>
                  <Link
                    to={l.to}
                    className="text-sm font-semibold text-background-50/85 hover:text-accent-300 transition-colors cursor-pointer"
                  >
                    {l.label}
                  </Link>
                  {l.sub && (
                    <div className="ml-3 mt-1 flex flex-col gap-1">
                      {l.sub.map((s) => (
                        <Link
                          key={s.to}
                          to={s.to}
                          className="text-xs text-background-50/55 hover:text-accent-300 transition-colors cursor-pointer"
                        >
                          {s.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="text-[11px] tracking-[0.2em] uppercase text-background-50/50 font-semibold mb-5">
              CONTACT
            </div>
            <ul className="flex flex-col gap-3 text-sm">
              <li className="flex items-start gap-3">
                <span className="w-5 h-5 mt-0.5 flex items-center justify-center text-accent-400">
                  <i className="ri-map-pin-2-line" />
                </span>
                <span className="text-background-50/85 leading-relaxed">{siteInfo.address}</span>
              </li>
              <li>
                <a
                  href={siteInfo.telLink}
                  className="flex items-center gap-3 text-background-50/85 hover:text-accent-300 cursor-pointer"
                >
                  <span className="w-5 h-5 flex items-center justify-center text-accent-400">
                    <i className="ri-phone-line" />
                  </span>
                  TEL · {siteInfo.tel}
                </a>
              </li>
              <li className="flex items-center gap-3 text-background-50/85">
                <span className="w-5 h-5 flex items-center justify-center text-accent-400">
                  <i className="ri-printer-line" />
                </span>
                FAX · {siteInfo.fax}
              </li>
              <li>
                <a
                  href={siteInfo.emailLink}
                  className="flex items-center gap-3 text-background-50/85 hover:text-accent-300 cursor-pointer"
                >
                  <span className="w-5 h-5 flex items-center justify-center text-accent-400">
                    <i className="ri-mail-line" />
                  </span>
                  {siteInfo.email}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-background-50/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-5">
            <p className="text-xs text-background-50/55">
              © {new Date().getFullYear()} {siteInfo.brandKo}. All rights reserved.
            </p>
            <Link
              to="/admin/login"
              className="text-[10px] text-background-50/25 hover:text-background-50/55 transition-colors cursor-pointer"
            >
              관리자
            </Link>
          </div>
          <div className="flex items-center gap-5 text-xs text-background-50/70">
            <a href="#" className="hover:text-accent-300 cursor-pointer">
              개인정보처리방침
            </a>
            <a href="#" className="hover:text-accent-300 cursor-pointer">
              이용약관
            </a>
            <a href="#" className="hover:text-accent-300 cursor-pointer">
              사이트맵
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}